import React from "react";
import { Autocomplete, Box, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFieldValues } from "./useReportFields";

const LIST = ["any_of", "none_of"];
const DAY = /^\d{4}-\d{2}-\d{2}$/;

// date pickers hold Dates; filters hold calendar days
export const toDay = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
    : null;
export const fromDay = (day) => {
  if (typeof day !== "string" || !DAY.test(day)) return null;
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const DayPicker = ({ label, value, onChange, minDay, maxDay, sx }) => (
  <DatePicker
    label={label}
    value={fromDay(value)}
    onChange={(date) => onChange(toDay(date))}
    minDate={fromDay(minDay) ?? undefined}
    maxDate={fromDay(maxDay) ?? undefined}
    slotProps={{
      textField: { size: "small", sx: { width: 168, ...sx } },
      field: { clearable: true },
      actionBar: { actions: ["clear", "today"] },
    }}
  />
);

/**
 * What a condition compares against, as its field's type and operator ask:
 * a picker of the field's values, typed text or a number, or a day.
 */
export const ValueEditor = ({ field, operator, value, onChange }) => {
  const picks = field && ["enum", "tags"].includes(field.type) && operator !== "contains";
  const { values, loading } = useFieldValues(field?.key, Boolean(picks));
  if (!field || !operator || ["is_empty", "is_not_empty"].includes(operator)) return null;
  const label = (v) => values.find((o) => o.value === v)?.label ?? String(v);

  if (field.type === "date" || field.type === "datetime") {
    if (operator === "between") {
      const [from, to] = Array.isArray(value) ? value : [null, null];
      return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <DayPicker label="From" value={from} maxDay={to} onChange={(d) => onChange([d, to])} />
          <DayPicker label="To" value={to} minDay={from} onChange={(d) => onChange([from, d])} />
        </Box>
      );
    }
    return <DayPicker label="Day" value={typeof value === "string" ? value : null} onChange={onChange} />;
  }

  if (field.type === "number") {
    const input = (v, set, l) => (
      <TextField size="small" type="number" label={l} value={v ?? ""} onChange={(e) => set(e.target.value)} sx={{ width: 140 }} />
    );
    if (operator === "between") {
      const [from, to] = Array.isArray(value) ? value : ["", ""];
      return (
        <Box sx={{ display: "flex", gap: 1 }}>
          {input(from, (v) => onChange([v, to]), "From")}
          {input(to, (v) => onChange([from, v]), "To")}
        </Box>
      );
    }
    return input(value, onChange, "Value");
  }

  if (picks) {
    const multiple = LIST.includes(operator);
    return (
      <Autocomplete
        size="small"
        multiple={multiple}
        freeSolo
        disableCloseOnSelect={multiple}
        loading={loading}
        options={values.map((o) => o.value)}
        getOptionLabel={label}
        value={multiple ? (Array.isArray(value) ? value : []) : value ?? null}
        onChange={(_, v) => onChange(v)}
        limitTags={3}
        renderInput={(params) => <TextField {...params} label={multiple ? "Values" : "Value"} />}
        sx={{ minWidth: 240, flex: "1 1 240px", maxWidth: 480 }}
      />
    );
  }

  return (
    <TextField
      size="small"
      label="Text"
      value={typeof value === "string" ? value : ""}
      onChange={(e) => onChange(e.target.value)}
      sx={{ minWidth: 240, flex: "1 1 240px", maxWidth: 480 }}
    />
  );
};
